import { ApiCustomer, ApiCustomerGroup } from '@vend/types'
import { fetchApiJson } from '@vend/utilities'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCustomerManager } from '../../../ng-compat/hooks'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { useTranslateCustomerGroup } from '../../helpers/useTranslateCustomerGroup'
import type { CustomerSearchProps } from './CustomerSearch'

const SEARCH_DEBOUNCE_MS = 300

export type CustomerSearchCustomer = ApiCustomer & {
  customerGroup?: ApiCustomerGroup
}

interface SearchResultData {
  id: string
  type: string
}

interface SearchResult {
  data: SearchResultData[]
  related: {
    customers: Record<string, ApiCustomer>
  }
}

const getCustomerLabel = (customer: ApiCustomer) =>
  customer.name || customer.email || customer.id

export const useCustomerSearch = (props: CustomerSearchProps) => {
  const [inputValue, setInputValue] = useState((props.searchValue || '').trim())
  const [customers, setCustomers] = useState<CustomerSearchCustomer[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const debounceRef = useRef<number>()
  const requestGenerationRef = useRef(0)
  const customerManager = useCustomerManager()
  const translateCustomerGroup = useTranslateCustomerGroup()
  const customerGroupsEnabled = getFeatureFromCache('customer_groups')

  const getCustomerWithGroup = useCallback(
    async (customer: ApiCustomer): Promise<CustomerSearchCustomer> => {
      if (!customerGroupsEnabled) return customer

      try {
        const customerGroup = await customerManager.getCustomerGroup(customer)

        return customerGroup
          ? {
              ...customer,
              customerGroup: translateCustomerGroup(customerGroup),
            }
          : customer
      } catch {
        return customer
      }
    },
    [customerGroupsEnabled, customerManager, translateCustomerGroup]
  )

  const options = useMemo(
    () => [
      {
        items: customers.map(customer => ({
          label: getCustomerLabel(customer),
          value: customer.id,
        })),
      },
    ],
    [customers]
  )

  const customersById = useMemo(
    () => new Map(customers.map(customer => [customer.id, customer])),
    [customers]
  )

  const handleInputValueChange = useCallback(
    (value: string) => {
      window.clearTimeout(debounceRef.current)
      setInputValue(value)
      props.onFetch?.(value.trim())

      const query = value.trim()
      setIsOpen(Boolean(query))
      if (!query) {
        requestGenerationRef.current += 1
        setCustomers([])
        setIsLoading(false)
        return
      }

      setCustomers([])
      setIsLoading(true)
      const requestGeneration = ++requestGenerationRef.current

      debounceRef.current = window.setTimeout(async () => {
        try {
          const { data, related }: SearchResult = await fetchApiJson(
            `/api/2.0/suggest?type=customer&page_size=30&include_related=true&q=${encodeURIComponent(
              query
            )}`
          )

          if (requestGeneration !== requestGenerationRef.current) return

          const fetchedCustomers = data
            .map(suggestion => related.customers[suggestion.id])
            .filter((customer): customer is ApiCustomer => Boolean(customer))
          const customersWithGroups = await Promise.all(
            fetchedCustomers.map(getCustomerWithGroup)
          )

          if (requestGeneration !== requestGenerationRef.current) return

          setCustomers(customersWithGroups)
        } catch {
          if (requestGeneration === requestGenerationRef.current) {
            setCustomers([])
          }
        } finally {
          if (requestGeneration === requestGenerationRef.current) {
            setIsLoading(false)
          }
        }
      }, SEARCH_DEBOUNCE_MS)
    },
    [getCustomerWithGroup, props]
  )

  const handleChange = useCallback(
    (value: string | string[]) => {
      const customerId = Array.isArray(value) ? value[0] : value
      setIsOpen(false)
      props.onSelect(customersById.get(customerId) || null)
    },
    [customersById, props]
  )

  useEffect(
    () => () => {
      window.clearTimeout(debounceRef.current)
      requestGenerationRef.current += 1
    },
    []
  )

  const handleOpened = useCallback((opened: boolean) => setIsOpen(opened), [])

  return {
    customersById,
    handleChange,
    handleInputValueChange,
    handleOpened,
    inputValue,
    isLoading,
    isOpen,
    options,
  }
}
